/**
 * Client-side PPTX image compressor — main-thread wrapper.
 *
 * Offloads all heavy work (JSZip, image decode/resize/encode) to a Web Worker
 * using OffscreenCanvas, keeping the UI thread free and responsive.
 *
 * The worker posts progress messages back so the caller can show a progress bar.
 */

const SIZE_THRESHOLD_MB = 50;
const WORKER_TIMEOUT_MS = 60000; // 60s — kill worker if no completion

/**
 * Compress images inside a PPTX file using a background Web Worker.
 *
 * @param {File} file - The PPTX File object
 * @param {function} onProgress - Optional callback({ stage, detail, percent })
 * @returns {Promise<File>} - Compressed File (or original if small / no images)
 */
export async function compressPptx(file, onProgress) {
  const sizeMB = file.size / (1024 * 1024);
  if (sizeMB < SIZE_THRESHOLD_MB) return file;

  console.error('[Compress] Starting compression for', sizeMB.toFixed(1) + 'MB file');

  return new Promise((resolve) => {
    let worker;
    let settled = false;
    let timer;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { worker?.terminate(); } catch { /* ignore */ }
      resolve(result);
    };

    try {
      worker = new Worker(
        new URL('./pptxCompressor.worker.js', import.meta.url),
        { type: 'module' }
      );
    } catch (err) {
      console.error('[Compress] Worker creation failed:', err);
      onProgress?.({ stage: 'error', detail: 'Worker unavailable', percent: 0 });
      resolve(file);
      return;
    }

    console.error('[Compress] Worker created successfully');

    // Timeout — if worker never completes, resolve with original file
    timer = setTimeout(() => {
      console.error('[Compress] Worker timed out after', WORKER_TIMEOUT_MS / 1000, 's');
      onProgress?.({ stage: 'error', detail: 'Compression timed out', percent: 0 });
      finish(file);
    }, WORKER_TIMEOUT_MS);

    worker.onmessage = (e) => {
      const msg = e.data;
      switch (msg.type) {
        case 'progress':
          onProgress?.(msg);
          console.error('[Compress]', msg.stage, msg.detail, msg.percent + '%');
          break;
        case 'done': {
          const newMB = msg.buffer.byteLength / (1024 * 1024);
          console.error('[Compress] Done:', sizeMB.toFixed(1) + 'MB ->', newMB.toFixed(1) + 'MB');
          finish(new File([msg.buffer], file.name, { type: file.type }));
          break;
        }
        case 'skip':
          console.error('[Compress] Skipped (no compressible images)');
          finish(file);
          break;
        case 'error':
          console.error('[Compress] Worker error:', msg.message);
          onProgress?.({ stage: 'error', detail: msg.message, percent: 0 });
          finish(file);
          break;
        default:
          break;
      }
    };

    worker.onerror = (err) => {
      console.error('[Compress] Worker onerror:', err?.message || err);
      finish(file);
    };

    // Read the file and transfer the buffer to the worker (zero-copy)
    file.arrayBuffer().then((buffer) => {
      console.error('[Compress] Sending', (buffer.byteLength / (1024 * 1024)).toFixed(1) + 'MB to worker');
      worker.postMessage({ type: 'compress', buffer, fileName: file.name }, [buffer]);
    });
  });
}
